import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Props } from '../@types';
import { AuthContextType, User } from '../@types/auth';
import { decodePayload } from '../utils/decodePayload';
import { LoginPaper } from '../components/Login';

const KEYCLOAK_ENDPOINT = (window as any).REACT_APP_OAUTH2_PROVIDER_REFRESH_ENDPOINT;

const AuthContext = createContext<AuthContextType>({
    user: undefined,
    isAuthenticated: false,
    signIn: (authToken: string, refreshToken: string) => console.log('signIn'),
    signOut: () => console.log('signOut'),
    setAuthToken: (token: string) => console.log('setToken'),
    setRefreshToken: (token: string) => console.log('setToken'),
    refreshTheToken: () => Promise.any(''),
});

const useProvideAuth = () => {
    const [user, setUser] = useState<User>();

    const getAuthToken = () => {
        return localStorage.getItem('auth-token');
    };

    const getRefreshToken = () => {
        return localStorage.getItem('refresh-token');
    };

    const signOut = () => {
        setUser(undefined);
        localStorage.removeItem('auth-token');
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('is-locked');
        window.location.assign('/');
    };

    const setAuthToken = (token: string) => {
        localStorage.setItem('auth-token', token);
        const updatedUser = Object.assign({}, user);
        updatedUser.accessToken = token;
        setUser(updatedUser);
    };

    const setRefreshToken = (token: string) => {
        localStorage.setItem('refresh-token', token);
        const updatedUser = Object.assign({}, user);
        updatedUser.refreshToken = token;
        setUser(updatedUser);
    };

    const signIn = async (accessToken: string, refreshToken: string) => {
        try {
            const { exp, preferred_username, family_name, given_name, email, realm_access, workplace } = decodePayload(accessToken);
            const refreshPayload = decodePayload(refreshToken);

            localStorage.setItem('auth-token', accessToken);
            localStorage.setItem('refresh-token', refreshToken);

            const resolvedUser = {
                username: preferred_username,
                name: `${family_name} ${given_name}`,
                email,
                roles: realm_access.roles,
                accessToken,
                refreshToken,
                exp,
                refreshExp: refreshPayload.exp,
                workplace,
                hasRole: (role: string) => realm_access.roles.some(r => r === role),
                isRefreshTimeExpired: () => new Date().getTime() > refreshPayload.exp * 1000,
            };
            setUser(resolvedUser);
        } catch (e) {
            console.error(e);
            signOut();
        }
    };

    const refreshTheToken = async () => {
        if (localStorage.getItem('is-locked') || !KEYCLOAK_ENDPOINT || !user) {
            return;
        }

        if (new Date().getTime() > user.refreshExp * 1000) {
            signOut();
            return;
        }

        const refreshTime = new Date();
        refreshTime.setMinutes(refreshTime.getMinutes() + 1);

        if (refreshTime.getTime() <= user.exp * 1000) {
            return;
        }
        localStorage.setItem('is-locked', 'true');

        try {
            const response = await fetch(KEYCLOAK_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    client_id: 'buszrent-crm',
                    refresh_token: getRefreshToken() || '',
                    grant_type: 'refresh_token',
                }),
            });

            if (response.status === 401) {
                signOut();
                return;
            }
            const body = await response.json();
            await signIn(body.access_token, body.refresh_token);
            localStorage.removeItem('is-locked');

            return body.access_token;
        } catch {
            signOut();
            return;
        }
    };

    useEffect(() => {
        const init = async () => {
            const accessToken = getAuthToken();
            const refreshToken = getRefreshToken() || '';

            if (accessToken) {
                await signIn(accessToken, refreshToken);
            }
        };
        init();
    }, []);

    return {
        user,
        isAuthenticated: user !== undefined && !!user.accessToken,
        signIn,
        signOut,
        setAuthToken,
        setRefreshToken,
        refreshTheToken,
    };
};

const useAuth = () => {
    return useContext(AuthContext);
};

const ProvideAuth = ({ children }: Props) => {
    const auth = useProvideAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const search = location.search;
    const authToken = new URLSearchParams(search).get('t');
    const refreshToken = new URLSearchParams(search).get('r') || '';
    const queryParams = new URLSearchParams(search);

    useEffect(() => {
        const initAuth = async () => {
            if (authToken && auth.signIn && typeof auth.signIn === 'function') {
                auth.setAuthToken(authToken);
                auth.setRefreshToken(refreshToken);
                await auth.signIn(authToken, refreshToken);

                queryParams.delete('t');
                queryParams.delete('r');
                navigate(location.pathname + '?' + queryParams.toString(), { replace: true });
            }
        };
        initAuth();
    }, []);

    if (!auth.user) {
        return <LoginPaper />;
    }

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export { useAuth, useProvideAuth, ProvideAuth };
